/* eslint-disable no-unused-expressions */
import React from 'react';
import { PropTypes } from 'prop-types';

import { styled, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Typography, Grid, Paper, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiChevronRight } from '@mdi/js';
import { darkTheme, Button } from 'daaas-ui';

import Banner from '../components/UI/Banner';
import ContentContainer from '../components/Layout/ContentContainer';
import ContentSection from '../components/Layout/ContentSection';
import BrandingDAS from '../components/Layout/BrandingDAS';

const Root = styled('div')(({ theme }) => ({
  '.MuiButton-outlined, .MuiButton-outlined:hover': {
    borderColor: alpha(theme.palette.primary.main, 1),
  },
  '& .banner__header': {
    fontSize: `5rem !important`,
    lineHeight: '0.9375',
    letterSpacing: '0.47',
    [theme.breakpoints.up('sm')]: {
      fontSize: `6rem !important`,
    },
  },

  '& .banner__slogan': {
    fontSize: `1.6875rem !important`,
    fontWeight: 700,

    [theme.breakpoints.up('sm')]: {
      fontSize: `2.03rem !important`,
    },
  },

  '& .landing-page__button': {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '350px',
    },
    height: '3.5rem !important',
  },

  '& .landing-page__envCard': {
    borderRadius: theme.spacing(1.25),
  },

  '& .landing-page__envCardBtn': {
    width: '100%',
    height: '3.5rem !important',
  },

  '& .landing-page__squigglyLine': {
    marginTop: theme.spacing(24),
    marginBottom: theme.spacing(4),
  },

  '& .landing-page__DAS': {
    color: darkTheme.palette.background.paper,
    fontSize: `4rem !important`,
    lineHeight: '0.9375',
    width: '90%',
    [theme.breakpoints.up('sm')]: {
      fontSize: `5rem !important`,
    },
  },

  '& .landing-page__slogan': {
    color: darkTheme.palette.background.paper,
    fontSize: '1.75rem',
    fontWeight: 700,
  },
}));

export default function LandingPage() {
  const { t, i18n } = useTranslation(['landing', 'common', 'dialogs']);

  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:overview')}`;
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('landing:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('landing:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('landing:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('landing:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('landing:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  return (
    <Root>
      <Banner>
        <Typography className="banner__header" variant="h1" color="textPrimary">
          {t('common:daaas')}
        </Typography>
        <Typography
          sx={{ mt: 2 }}
          className="banner__slogan"
          variant="h1"
          component="h2"
          color="textPrimary"
        >
          {t('slogan')}
        </Typography>
      </Banner>
      <ContentContainer paddingBottom={false}>
        <ContentSection>
          <Typography sx={{ mb: 2 }}>{t('paragraph1')}</Typography>
          <ul>
            <li>
              <Typography>{t('list1')}</Typography>
            </li>
            <li>
              <Typography>{t('list2')}</Typography>
            </li>
            <li>
              <Typography>{t('list3')}</Typography>
            </li>
            <li>
              <Typography>{t('list4')}</Typography>
            </li>
          </ul>
          <Box
            sx={{
              textAlign: {
                xs: 'center',
                md: 'left',
              },
            }}
          >
            <Button
              sx={{ mt: 4, mr: 4 }}
              className="landing-page__button"
              variant="contained"
              color="primary"
              endIcon={<Icon path={mdiChevronRight} size={1} />}
              href={t('common:hrefMsFormsGetStarted')}
              target="_blank"
            >
              {t('getStartedButton')}
            </Button>{' '}
            <Button
              sx={{ mt: 4 }}
              className="landing-page__button"
              variant="outlined"
              color="primary"
              endIcon={<Icon path={mdiChevronRight} size={1} />}
              href={`${t('common:/urlPrefix')}${t('common:/aboutdas')}`}
            >
              {t('common:daaasBreadCrumb')}
            </Button>
          </Box>
        </ContentSection>
        <ContentSection>
          <Typography sx={{ mb: 4 }}>{t('heading1')}</Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={10} md={4} sx={{ display: 'flex' }}>
              <EnvironmentCard
                id="aawEnvCard"
                title={t('common:aaw')}
                description={t('aawDesc')}
                buttonTo={`${t('common:/urlPrefix')}${t('common:/aaw')}`}
                buttonText={t('aawBtn')}
              />
            </Grid>
            <Grid item xs={12} sm={10} md={4} sx={{ display: 'flex' }}>
              <EnvironmentCard
                id="caeEnvCard"
                title={t('common:cae')}
                description={t('caeDesc')}
                buttonTo={`${t('common:/urlPrefix')}${t('common:/cae')}`}
                buttonText={t('caeBtn')}
              />
            </Grid>
            <Grid item xs={12} sm={10} md={4} sx={{ display: 'flex' }}>
              <EnvironmentCard
                id="gaeEnvCard"
                title={t('common:gae')}
                description={t('gaeDesc')}
                buttonTo={`${t('common:/urlPrefix')}${t('common:/gae')}`}
                buttonText={t('gaeBtn')}
              />
            </Grid>
          </Grid>
        </ContentSection>
      </ContentContainer>
      <BrandingDAS
        button={
          <Button
            className="landing-page__button"
            color="primary"
            variant="contained"
            endIcon={<Icon path={mdiChevronRight} size={1} />}
            href={t('common:hrefMsFormsGetStarted')}
            target="_blank"
          >
            {t('getStartedButton')}
          </Button>
        }
      />
    </Root>
  );
}

const CardRoot = styled('div')(() => ({
  display: 'flex',

  '& .env-card__button': {
    width: '100%',
    height: '3.5rem !important',
  },
}));
function EnvironmentCard({ id, title, description, buttonTo, buttonText }) {
  return (
    <CardRoot>
      <Paper sx={{ p: 2, display: 'flex' }} className="das-paper" elevation={3}>
        <Grid
          container
          display="flex"
          justifyContent="space-between"
          flexDirection="column"
        >
          <Grid item>
            <Typography
              id={`${id}-title`}
              variant="h5"
              component="h3"
              sx={{ mb: 3, width: '50%' }}
            >
              {title}
            </Typography>
            <Typography sx={{ mb: 4 }}>{description}</Typography>
          </Grid>
          <Grid item>
            <Button
              aria-describedby={`${id}-title`}
              className="env-card__button"
              color="primary"
              variant="contained"
              endIcon={<Icon path={mdiChevronRight} size={1} />}
              component={RouterLink}
              to={buttonTo}
            >
              {buttonText}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </CardRoot>
  );
}

EnvironmentCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonTo: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
};
